import React, { useLayoutEffect } from 'react';
import cn from "classnames";
import style from "./common.module.scss";
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { useTranslation } from "react-i18next";
import { useScreenSize } from '../../../hooks/useScreenSize';
import { shortCompanyName } from "../../../utils/shortenName";

const SLAComplianceByExpeditor = ({ item }) => {
    const { isMobileView } = useScreenSize();
    const { t } = useTranslation();

    const legendItems = [
        { name: t("analytics.onTime"), color: "#4E7CFF" },
        { name: t("analytics.late"), color: "#22C0FF" }
    ];

    useLayoutEffect(() => {
        let root = am5.Root.new('chartdiv-expeditor-sla');
        root.setThemes([am5themes_Animated.new(root)]);
        root._logo.dispose();

        let chart = root.container.children.push(
            am5xy.XYChart.new(root, {
                layout: root.verticalLayout,
                wheelY: "zoomX",
            })
        );

        chart.set("wheelable", true);
        if (isMobileView) {
            chart.set("paddingLeft", 1);
        }

        let xAxis = chart.xAxes.push(
            am5xy.CategoryAxis.new(root, {
                categoryField: 'expeditorId',
                renderer: am5xy.AxisRendererX.new(root, {
                    minGridDistance: 30,
                }),
                tooltip: am5.Tooltip.new(root, {
                    labelText: "{expeditorName}"
                }),
            })
        );

        xAxis.get("renderer").grid.template.setAll({
            strokeDasharray: [4, 4],
            strokeOpacity: 1,
            stroke: am5.color(0xe7e7f4),
        });

        xAxis.get("renderer").labels.template.setAll({
            fill: am5.color("#8E8E8E"),
            fontSize: 12,
        });

        xAxis.get("renderer").labels.template.adapters.add("text", (text, target) => {
            const dataItem = target.dataItem;
            if (dataItem) {
                return dataItem.dataContext.shortExpeditorName;
            }
            return text;
        });

        const processedData = item.map(entry => {
            const total = entry.success + entry.failure;
            const percentSuccess = total > 0 ? (entry.success / total) * 100 : 0;
            const percentFailure = total > 0 ? (entry.failure / total) * 100 : 0;
            return {
                ...entry,
                expeditorName: entry.expeditorName,
                shortExpeditorName: shortCompanyName(entry.expeditorName),
                successPercent: percentSuccess,
                failurePercent: percentFailure,
            };
        });

        xAxis.data.setAll(processedData);

        let yAxis = chart.yAxes.push(
            am5xy.ValueAxis.new(root, {
                renderer: am5xy.AxisRendererY.new(root, {}),
            })
        );

        yAxis.get("renderer").grid.template.setAll({
            strokeDasharray: [4, 4],
            strokeOpacity: 1,
            stroke: am5.color(0xe7e7f4),
        });

        yAxis.get("renderer").labels.template.setAll({
            fill: am5.color("#8E8E8E"),
            fontSize: 12,
        });

        yAxis.children.unshift(
            am5.Label.new(root, {
                rotation: -90,
                text: t("analytics.requestCount"),
                y: am5.p50,
                centerX: am5.p50,
                centerY: am5.p50,
                fontSize: 14,
                fill: am5.color("#5A5959"),
            }),
        );

        const cursor = chart.set(
            "cursor",
            am5xy.XYCursor.new(root, {
                behavior: "zoomX",
                xAxis: xAxis,
            }),
        );
        cursor.lineY.set("visible", false);

        const createSeries = (name, field, percentField, color) => {
            let series = chart.series.push(
                am5xy.ColumnSeries.new(root, {
                    name: name,
                    xAxis: xAxis,
                    yAxis: yAxis,
                    categoryXField: "expeditorId",
                    valueYField: field,
                    stacked: true,
                })
            );

            series.columns.template.setAll({
                fill: am5.color(color),
                stroke: am5.color(color),
                maxWidth: 80,
            });

            series.set(
                "tooltip",
                am5.Tooltip.new(root, {
                    labelText: `{name}: {valueY} ({${percentField}.formatNumber('#.0')}%)`
                }),
            );

            series.data.setAll(processedData);
        };

        createSeries(t("analytics.onTime"), "success", "successPercent", "#4E7CFF");
        createSeries(t("analytics.late"), "failure", "failurePercent", "#22C0FF");

        if (isMobileView) {
            xAxis.get("renderer").labels.template.set("visible", false);
            xAxis.get("renderer").grid.template.set("visible", false);
            xAxis.get("renderer").ticks.template.set("visible", false);

            let labelSeries = chart.series.push(
                am5xy.ColumnSeries.new(root, {
                    name: "ExpeditorLabel",
                    xAxis: xAxis,
                    yAxis: yAxis,
                    categoryXField: "expeditorId",
                    valueYField: "success",
                    clustered: false,
                    stacked: true,
                })
            );

            labelSeries.columns.template.setAll({
                fillOpacity: 0,
                strokeOpacity: 0,
                width: 1,
            });

            labelSeries.bullets.push(() => {
                return am5.Bullet.new(root, {
                    locationY: 0,
                    sprite: am5.Label.new(root, {
                        text: "{categoryX}",
                        rotation: -90,
                        centerY: am5.p50,
                        populateText: true,
                        fontSize: 14,
                        fill: am5.color(0x333333),
                    }),
                });
            });

            labelSeries.data.setAll(processedData);            
        }

        return () => {
            root.dispose();
        };
    }, [item, isMobileView]);

    return (
        <div className={style.content}>
            <div className={style.filter}>
                <span className={style.filter_title}>{t("analytics.expeditorPerformance")}</span>
            </div>

            <div className={cn(style.area__namesMobile)}>
                {legendItems.map((legendItem, index) => (
                    <div key={legendItem.name} style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                        <div
                            className={style.area_icon}
                            style={{ backgroundColor: legendItem.color }}
                        ></div>
                        <div className={style.area_type}>{legendItem.name}</div>
                    </div>
                ))}
            </div>

            <div className={style.area}>
                <div id="chartdiv-expeditor-sla" className={style.area_chart}></div>
                <div className={style.area_names}>
                    {legendItems.map((legendItem, index) => (
                        <div key={legendItem.name} style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                            <div
                                className={style.area_icon}
                                style={{ backgroundColor: legendItem.color }}
                            ></div>
                            <div className={style.area_type}>{legendItem.name}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default SLAComplianceByExpeditor;
